import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { supabase } from './supabaseClient';
import './NewsletterSubscriptions.css';

const NewsletterSubscriptions = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);

  // Fetch newsletter subscriptions from Supabase
  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Fetching newsletter subscriptions...');
        
        const { data, error: fetchError } = await supabase
          .from('newsletter_subscriptions')
          .select('*')
          .order('created_at', { ascending: false });

        if (fetchError) {
          console.error('Supabase error:', fetchError);
          throw fetchError;
        }

        console.log('Fetched subscriptions:', data);

        // Format subscription data
        const formattedSubscriptions = (data || []).map((subscription) => ({
          id: subscription.id,
          email: subscription.email || 'N/A',
          status: subscription.status || 'active',
          subscriptionSource: subscription.subscription_source || 'website',
          isVerified: subscription.is_verified || false,
          createdAt: subscription.created_at 
            ? new Date(subscription.created_at).toLocaleString('en-US', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            : 'N/A',
          updatedAt: subscription.updated_at 
            ? new Date(subscription.updated_at).toLocaleString('en-US', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            : 'N/A',
          unsubscribedAt: subscription.unsubscribed_at 
            ? new Date(subscription.unsubscribed_at).toLocaleString('en-US', { 
                year: 'numeric', 
                month: '2-digit', 
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit'
              })
            : null,
          unsubscribeReason: subscription.unsubscribe_reason || null,
          rawSubscription: subscription
        }));

        setAllSubscriptions(formattedSubscriptions);
      } catch (err) {
        console.error('Error fetching newsletter subscriptions:', err);
        setError(err.message || 'Failed to fetch newsletter subscriptions');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, []);

  // Filter subscriptions based on search and filters
  const filteredSubscriptions = allSubscriptions.filter((subscription) => {
    const matchesSearch = 
      subscription.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || subscription.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Pagination
  const totalPages = Math.ceil(filteredSubscriptions.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedSubscriptions = filteredSubscriptions.slice(startIndex, endIndex);

  // Handle status update
  const handleStatusUpdate = async (subscriptionId, newStatus) => {
    try {
      const updateData = {
        status: newStatus,
        updated_at: new Date().toISOString()
      };

      if (newStatus === 'unsubscribed') {
        updateData.unsubscribed_at = new Date().toISOString();
      } else if (newStatus === 'active' && updateData.unsubscribed_at) {
        updateData.unsubscribed_at = null;
        updateData.unsubscribe_reason = null;
      }

      const { error: updateError } = await supabase
        .from('newsletter_subscriptions')
        .update(updateData)
        .eq('id', subscriptionId);

      if (updateError) throw updateError;

      // Update local state
      setAllSubscriptions(prev => prev.map(sub => 
        sub.id === subscriptionId 
          ? { 
              ...sub, 
              status: newStatus, 
              unsubscribedAt: newStatus === 'unsubscribed' ? updateData.unsubscribed_at : null
            }
          : sub
      ));
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Failed to update status: ' + err.message);
    }
  };

  // Handle delete subscription - show confirmation modal
  const handleDeleteClick = (subscription) => {
    setSubscriptionToDelete(subscription);
    setShowDeleteModal(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!subscriptionToDelete) return;

    try {
      const { error: deleteError } = await supabase
        .from('newsletter_subscriptions')
        .delete()
        .eq('id', subscriptionToDelete.id);

      if (deleteError) throw deleteError;

      // Update local state
      setAllSubscriptions(prev => prev.filter(sub => sub.id !== subscriptionToDelete.id));
      
      // Show success modal
      setShowDeleteModal(false);
      setShowDeleteSuccess(true);
      setSubscriptionToDelete(null);
      
      // Auto-close success modal after 3 seconds
      setTimeout(() => {
        setShowDeleteSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error deleting subscription:', err);
      setShowDeleteModal(false);
      alert('Failed to delete subscription: ' + err.message);
      setSubscriptionToDelete(null);
    }
  };

  // Cancel delete
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setSubscriptionToDelete(null);
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active': return 'newsletter-status-active';
      case 'unsubscribed': return 'newsletter-status-unsubscribed';
      case 'bounced': return 'newsletter-status-bounced';
      case 'invalid': return 'newsletter-status-invalid';
      default: return 'newsletter-status-default';
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    const headers = ['Email', 'Status', 'Source', 'Verified', 'Subscribed Date', 'Unsubscribed Date'];
    const rows = filteredSubscriptions.map(sub => [
      sub.email,
      sub.status,
      sub.subscriptionSource,
      sub.isVerified ? 'Yes' : 'No',
      sub.createdAt,
      sub.unsubscribedAt || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-subscriptions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <AdminLayout pageTitle="Newsletter Subscriptions">
        <div className="admin-newsletter-page">
          <div className="admin-loading-container">
            <div className="admin-loading-spinner"></div>
            <p>Loading newsletter subscriptions...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout pageTitle="Newsletter Subscriptions">
        <div className="admin-newsletter-page">
          <div className="admin-error-container">
            <div className="admin-error-icon">⚠️</div>
            <h3 className="admin-error-title">Connection Error</h3>
            <p className="admin-error-message">
              {error.includes('permission') || error.includes('policy') || error.includes('RLS')
                ? 'RLS Policy Error: The newsletter_subscriptions table may have Row Level Security enabled. Please check your Supabase RLS policies or run the updated SQL script to set up proper permissions.'
                : error.includes('relation') || error.includes('does not exist')
                ? 'Table Not Found: The newsletter_subscriptions table does not exist. Please run the SQL script: supabase_newsletter_subscriptions_table.sql in your Supabase SQL Editor.'
                : `Error: ${error}`}
            </p>
            <div className="admin-error-details">
              <p><strong>Possible solutions:</strong></p>
              <ul>
                <li>Make sure the <code>newsletter_subscriptions</code> table exists in Supabase</li>
                <li>Run the SQL script: <code>supabase_newsletter_subscriptions_table.sql</code> in Supabase SQL Editor</li>
                <li>Check RLS policies allow admin access (the script includes public read policies)</li>
                <li>Verify Supabase credentials in .env file (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY)</li>
                <li>Check browser console for detailed error messages</li>
              </ul>
            </div>
            <button 
              className="admin-retry-button"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout pageTitle="Newsletter Subscriptions">
      {/* Delete Confirmation Modal */}
      {showDeleteModal && subscriptionToDelete && (
        <div className="newsletter-delete-modal-overlay" onClick={cancelDelete}>
          <div className="newsletter-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="newsletter-delete-modal-content">
              <div className="newsletter-delete-modal-icon">🗑️</div>
              <h3 className="newsletter-delete-modal-title">Delete Subscription?</h3>
              <p className="newsletter-delete-modal-message">
                Are you sure you want to delete the subscription for <strong>{subscriptionToDelete.email}</strong>? 
                This action cannot be undone.
              </p>
              <div className="newsletter-delete-modal-actions">
                <button 
                  className="newsletter-delete-modal-button cancel-button"
                  onClick={cancelDelete}
                >
                  Cancel
                </button>
                <button 
                  className="newsletter-delete-modal-button confirm-button"
                  onClick={confirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Modal */}
      {showDeleteSuccess && (
        <div className="newsletter-delete-modal-overlay" onClick={() => setShowDeleteSuccess(false)}>
          <div className="newsletter-delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="newsletter-delete-modal-content">
              <div className="newsletter-delete-modal-icon success-icon">✓</div>
              <h3 className="newsletter-delete-modal-title">Successfully Deleted!</h3>
              <p className="newsletter-delete-modal-message">
                The subscription has been deleted successfully.
              </p>
              <button 
                className="newsletter-delete-modal-button confirm-button"
                onClick={() => setShowDeleteSuccess(false)}
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="admin-newsletter-page">
        {/* Header */}
        <div className="admin-newsletter-header">
          <div className="admin-newsletter-header-left">
            <h1 className="admin-newsletter-title">Newsletter Subscriptions</h1>
            <p className="admin-newsletter-subtitle">
              Manage newsletter email subscriptions
            </p>
          </div>
          <div className="admin-newsletter-header-right">
            <div className="admin-newsletter-stats">
              <div className="admin-newsletter-stat-item">
                <span className="stat-label">Total</span>
                <span className="stat-value">{allSubscriptions.length}</span>
              </div>
              <div className="admin-newsletter-stat-item">
                <span className="stat-label">Active</span>
                <span className="stat-value active-count">
                  {allSubscriptions.filter(s => s.status === 'active').length}
                </span>
              </div>
            </div>
            <button 
              className="admin-export-button"
              onClick={handleExportCSV}
            >
              📥 Export CSV
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="admin-newsletter-filters">
          <div className="admin-newsletter-search">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              className="admin-newsletter-search-input"
            />
          </div>
          <div className="admin-newsletter-filter-group">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="admin-newsletter-filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="unsubscribed">Unsubscribed</option>
              <option value="bounced">Bounced</option>
              <option value="invalid">Invalid</option>
            </select>
          </div>
          <div className="admin-newsletter-filter-group">
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="admin-newsletter-filter-select"
            >
              <option value="10">10 per page</option>
              <option value="25">25 per page</option>
              <option value="50">50 per page</option>
              <option value="100">100 per page</option>
            </select>
          </div>
        </div>

        {/* Table View */}
        {filteredSubscriptions.length > 0 ? (
          <>
            <div className="admin-newsletter-table-wrapper">
              <table className="admin-newsletter-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Status</th>
                    <th>Source</th>
                    <th>Verified</th>
                    <th>Subscribed Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSubscriptions.map((subscription) => (
                    <tr key={subscription.id} className="admin-newsletter-table-row">
                      <td className="admin-table-cell">
                        <div className="admin-newsletter-email">{subscription.email}</div>
                      </td>
                      <td className="admin-table-cell">
                        <span className={`newsletter-status-badge ${getStatusBadgeClass(subscription.status)}`}>
                          {subscription.status}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <span className="admin-newsletter-source">{subscription.subscriptionSource}</span>
                      </td>
                      <td className="admin-table-cell">
                        <span className={subscription.isVerified ? 'newsletter-verified' : 'newsletter-unverified'}>
                          {subscription.isVerified ? '✓ Yes' : '✗ No'}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <div className="admin-newsletter-date">{subscription.createdAt}</div>
                      </td>
                      <td className="admin-table-cell">
                        <div className="admin-newsletter-actions">
                          {subscription.status === 'active' ? (
                            <button
                              className="admin-action-button unsubscribe-button"
                              onClick={() => handleStatusUpdate(subscription.id, 'unsubscribed')}
                              title="Unsubscribe"
                            >
                              Unsubscribe
                            </button>
                          ) : (
                            <button
                              className="admin-action-button subscribe-button"
                              onClick={() => handleStatusUpdate(subscription.id, 'active')}
                              title="Reactivate"
                            >
                              Reactivate
                            </button>
                          )}
                          <button
                            className="admin-action-button delete-button"
                            onClick={() => handleDeleteClick(subscription)}
                            title="Delete"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="admin-newsletter-pagination">
                <button
                  className="admin-pagination-button"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span className="admin-pagination-info">
                  Page {currentPage} of {totalPages} ({filteredSubscriptions.length} total)
                </span>
                <button
                  className="admin-pagination-button"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="admin-newsletter-empty">
            <div className="admin-empty-icon">📧</div>
            <h3 className="admin-empty-title">No subscriptions found</h3>
            <p className="admin-empty-text">
              {searchQuery || statusFilter !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'No newsletter subscriptions yet'}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default NewsletterSubscriptions;
